<script setup lang="ts">
    import { computed } from 'vue'

    interface Props {
        url: string
        maxLength?: number
        domainClass?: string
        target?: '_blank' | '_self' | '_parent' | '_top'
        rel?: string
        muted?: boolean
    }

    const props = withDefaults(defineProps<Props>(), {
        maxLength: 60,
        domainClass: undefined,
        target: '_blank',
        rel: 'noopener noreferrer',
        muted: false,
    })

    const parsedUrl = computed(() => {
        try {
            const urlObj = new URL(props.url)
            let hostname = urlObj.hostname

            // Remove www. prefix
            if (hostname.startsWith('www.')) hostname = hostname.substring(4)

            // Get pathname and remove trailing slash
            let pathname = urlObj.pathname
            if (pathname === '/') pathname = ''

            // Split pathname into segments
            const segments = pathname
                .split('/')
                .filter((segment) => segment.length > 0)

            return {
                domain: hostname,
                segments,
                fullPath: hostname + pathname,
            }
        } catch {
            // If URL parsing fails, return the original URL
            return {
                domain: props.url,
                segments: [],
                fullPath: props.url,
            }
        }
    })

    const truncatedUrl = computed(() => {
        if (parsedUrl.value.fullPath.length <= props.maxLength)
            return parsedUrl.value

        // If too long, truncate segments from the beginning
        const domain = parsedUrl.value.domain
        const allSegments = parsedUrl.value.segments

        // Always show at least the last segment
        if (allSegments.length === 0) {
            return {
                domain,
                segments: [],
                fullPath: domain,
            }
        }

        const segments: string[] = []

        // Calculate from the end
        let currentLength = domain.length

        // Try to fit segments from the end
        for (let i = allSegments.length - 1; i >= 0; i--) {
            const segment = allSegments[i]

            // Always include at least the last segment
            if (segments.length === 0) {
                currentLength += segment.length + 1
                segments.unshift(segment)
            } else if (currentLength + segment.length + 1 <= props.maxLength) {
                currentLength += segment.length + 1
                segments.unshift(segment)
            } else {
                segments.unshift('...')
                break
            }
        }

        return {
            domain,
            segments,
            fullPath: domain + '/' + segments.join('/'),
        }
    })
</script>

<template>
    <Component
        :is="muted ? 'span' : 'a'"
        class="pk-url"
        :href="muted ? undefined : props.url"
        :target="muted ? undefined : props.target"
        :rel="muted ? undefined : props.rel">
        <span :class="['pk-url__domain', domainClass]">{{
            truncatedUrl.domain
        }}</span>
        <template
            v-for="(segment, index) in truncatedUrl.segments"
            :key="index">
            <span class="pk-url__separator">›</span>
            <span class="pk-url__segment">{{ segment }}</span>
        </template>
    </Component>
</template>

<style lang="scss">
    .pk-url {
        display: inline-flex;
        align-items: center;
        gap: 0.5ch;
    }
</style>
